/**
 * Fetch a user's Reklaim data and sync all Braze user attributes.
 *
 * WHY THIS EXISTS (REK-1403 follow-up): the original sync ran inside
 * `onAuthStateChange`'s `SIGNED_IN` handler and read `profiles` with the
 * Supabase client *before the client's auth session was applied*. Per the
 * documented gotcha in `AuthProvider` (see `checkAccountLocked`), RLS-gated
 * reads fail at that moment, so the profile fetch returned no row and NONE of
 * the profile-derived attributes were written (only `email`, which needs no DB
 * read). It also skipped the `INITIAL_SESSION` / magic-link path entirely.
 *
 * This function is called from a `user?.id`-keyed effect instead — which runs
 * after the session is established (same place the onboarding-status query
 * already works) and fires for every login path. The Supabase client is
 * INJECTED (never imported at module load) so unit tests can pass a mock and CI
 * doesn't need `VITE_SUPABASE_URL` at import time.
 *
 * SAFETY: writes per-user attributes only — no Braze events, no campaign/Canvas
 * send. See `.cursor/rules/braze-safety.mdc`.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { brazeSetAttribute } from './braze';
import {
  syncProfileAttributesToBraze,
  type BrazeSyncProfile,
  type BrazeProfileSetters,
} from './braze-profile-sync';
import { safeError } from '@/lib/safeLog';

const PROFILE_COLUMNS =
  'available_points, staked_points, onboarding_completed_at, full_name, country, phone_number, phone_verified, created_at, signup_source, signup_medium, referred_by, referral_code, lifetime_points, streak_count, s4r_eligible, last_login_at';

/** Profile row shape read here: the sync fields + the three legacy engagement columns. */
type ProfileRow = BrazeSyncProfile & {
  available_points?: number | null;
  staked_points?: number | null;
  onboarding_completed_at?: string | null;
};

/** Injectable Braze setters — defaults bind to the real wrappers. */
export interface BrazeUserSyncDeps {
  setAttribute: (key: string, value: string | number | boolean | Date | null) => void;
  syncProfile: (profile: BrazeSyncProfile, setters?: BrazeProfileSetters) => void;
}

const defaultDeps: BrazeUserSyncDeps = {
  setAttribute: brazeSetAttribute,
  syncProfile: syncProfileAttributesToBraze,
};

/**
 * Read the user's profile + engagement counts and push them to Braze. Each
 * sub-sync is independent and best-effort: a failure in one is logged and does
 * not block the others. Resolves once all have settled.
 */
export async function syncUserAttributesToBraze(
  client: SupabaseClient,
  userId: string,
  deps: BrazeUserSyncDeps = defaultDeps,
): Promise<void> {
  const syncProfile = async () => {
    const { data, error } = await client
      .from('profiles')
      .select(PROFILE_COLUMNS)
      .eq('id', userId)
      .single();
    if (error) throw error;
    const profile = data as ProfileRow | null;
    if (!profile) return;
    deps.setAttribute('available_points', profile.available_points ?? 0);
    deps.setAttribute('staked_points', profile.staked_points ?? 0);
    deps.setAttribute(
      'onboarding_status',
      profile.onboarding_completed_at ? 'completed' : 'in_progress',
    );
    // Native fields (first/last name, phone, country) + remaining profile-derived
    // custom attributes. Phone is gated on phone_verified === true in the helper.
    deps.syncProfile(profile);
  };

  const syncJackpot = async () => {
    const { data, error } = await client.rpc('get_user_total_jackpot_entries', {
      user_uuid: userId,
    });
    if (error) throw error;
    deps.setAttribute('jackpot_entries', (data as number | null) ?? 0);
  };

  const syncReferrals = async () => {
    const { count, error } = await client
      .from('referrals')
      .select('id', { count: 'exact', head: true })
      .eq('referrer_id', userId);
    if (error) throw error;
    deps.setAttribute('referrals_count', count ?? 0);
  };

  const syncSurveys = async () => {
    const { count, error } = await client
      .from('user_knowledge')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId)
      .like('attribute_key', 'survey_qm_%');
    if (error) throw error;
    deps.setAttribute('surveys_completed', count ?? 0);
  };

  const results = await Promise.allSettled([
    syncProfile(),
    syncJackpot(),
    syncReferrals(),
    syncSurveys(),
  ]);
  for (const r of results) {
    if (r.status === 'rejected') {
      safeError('[Braze] user attribute sync sub-task failed (non-fatal):', r.reason);
    }
  }
}
